import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Form, Input, InputNumber, Modal, Progress, Select, Space, Table, Tag, message } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { useApi } from "../../helpers/useApi";
import { fmtItem } from "../../helpers/format";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DocLink, type DocLinkType } from "../../components/DocLink";
import { coveredQty, expectedQty, outstandingQty } from "./coverage";
import type { ReceiptDiscrepancy, DispositionType, DiscrepancyDocument } from "../../api-models";

// Only the human decisions are offered here. ACCEPTED_RECEIPT, DAMAGE_QUARANTINE and
// RETURN_IN_TRANSIT are recorded automatically when the receipt posts (spec §12.4).
const MANUAL_DISPOSITIONS: { value: DispositionType; label: string; help: string }[] = [
  {
    value: "APPROVED_LOSS",
    label: "Approved loss",
    help: "The quantity is gone: posted from in-transit to the loss sink. Requires a reason and is audited as a write-off decision.",
  },
  {
    value: "REVERSAL",
    label: "Reversal to source",
    help: "The dispatch never really left: the remainder goes back to where it was dispatched from.",
  },
  {
    value: "REMAINING_DELIVERY",
    label: "Still expected",
    help: "Documentation only — no stock moves, and this alone cannot close the discrepancy. The eventual receipt does that.",
  },
];

/**
 * A GIN Direct's dispute is with the supplier over what was delivered, not with a carrier
 * over what survived the journey (gin_direct.go). Nothing was ever dispatched, so there is
 * no in-transit balance to drain and nowhere for a reversal to send anything back to — the
 * server refuses REVERSAL outright, and a write-off here posts no stock at all. The wording
 * follows.
 */
const SUPPLIER_DISPUTE_DISPOSITIONS: typeof MANUAL_DISPOSITIONS = [
  {
    value: "APPROVED_LOSS",
    label: "Write off against the supplier",
    help: "The quantity is not coming — refused at the door, or never delivered. No stock moves, because none of it ever entered GI's custody; recording it here closes the dispute and is audited as a write-off decision.",
  },
  {
    value: "REMAINING_DELIVERY",
    label: "Still expected",
    help: "Documentation only — the supplier still owes this quantity. It cannot close the dispute on its own; the eventual delivery does that.",
  },
];

export default function DiscrepancyDetailPage() {
  const { id } = useParams();
  const { user } = useUser();
  const { request, loading } = useApi();
  const [d, setD] = useState<ReceiptDiscrepancy | null>(null);
  const [docs, setDocs] = useState<DiscrepancyDocument[]>([]);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const canResolve = hasAnyGrant(user, "Discrepancies.Resolve");

  async function load() {
    setD(await request<ReceiptDiscrepancy>({ url: `/discrepancies/${id}` }));
  }

  useEffect(() => {
    load();
    // The source line id alone is a dead end on screen. The server resolves it to the
    // documents behind it, which differ by source type (discrepancy.go).
    request<DiscrepancyDocument[]>({ url: `/discrepancies/${id}/documents` }, { silent: true })
      .then(setDocs)
      .catch(() => setDocs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function addDisposition() {
    const values = await form.validateFields();
    await request({ method: "POST", url: `/discrepancies/${id}/dispositions`, data: values });
    message.success("Disposition recorded.");
    setOpen(false);
    form.resetFields();
    load();
  }

  if (!d) return null;

  // Mirrors the server's coveredQty: "still expected" is a promise, not a settlement.
  const accounted = coveredQty(d);
  const outstanding = outstandingQty(d);
  const expected = expectedQty(d);
  const supplierDispute = d.sourceType === "GIN_DIRECT_LINE";
  const dispositions = supplierDispute ? SUPPLIER_DISPUTE_DISPOSITIONS : MANUAL_DISPOSITIONS;

  return (
    <div>
      <PageHeader
        title={`Discrepancy · ${d.item ? fmtItem(d.item) : d.itemId}`}
        subtitle={`Opened ${new Date(d.openedAt).toLocaleString()}`}
        extra={
          <>
            <Tag color={d.status === "OPEN" ? "orange" : "green"} style={{ marginRight: 8 }}>
              {d.status}
            </Tag>
            {d.status === "OPEN" && canResolve && (
              <Button
                type="primary"
                data-tour="discrepancy-disposition"
                onClick={() => {
                  form.setFieldsValue({ type: "APPROVED_LOSS", qty: outstanding, reason: "" });
                  setOpen(true);
                }}
              >
                Record disposition
              </Button>
            )}
          </>
        }
      />

      <Alert
        style={{ marginBottom: 16 }}
        type={d.status === "OPEN" ? "warning" : "success"}
        showIcon
        message={d.status === "OPEN" ? `${outstanding} unit(s) unaccounted for` : "Fully accounted for"}
        description={
          supplierDispute
            ? d.status === "OPEN"
              ? `A supplier dispute is held against the whole order. It closes once every unit ordered is in stock, in quarantine, or written off — quantity refused at the door and quantity never delivered both stay outstanding until someone resolves them. To close the remaining ${outstanding}: post a GIN Direct when the supplier delivers it, or record it as an approved loss to write it off.`
              : "Every ordered unit is in stock, in quarantine, or written off."
            : d.status === "OPEN"
              ? "A discrepancy closes only once every dispatched unit has a reason AND the dispatch's in-transit location holds none of the item. Both gates are checked by the server."
              : "Every dispatched unit carries a reason and the in-transit location is empty."
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Progress
          percent={Math.min(100, Math.round((accounted / d.dispatchedQty) * 100))}
          status={d.status === "OPEN" ? "active" : "success"}
          style={{ marginBottom: 12 }}
        />
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Item">{fmtItem(d.item)}</Descriptions.Item>
          <Descriptions.Item label={supplierDispute ? "Order and deliveries" : "Source document"}>
            {docs.length === 0 ? (
              <span>
                {d.sourceType.replace(/_/g, " ").toLowerCase()} #{d.sourceLineId}
              </span>
            ) : (
              <Space wrap size={4}>
                {docs.map((doc) => (
                  <span key={`${doc.docType}-${doc.id}`}>
                    <DocLink docType={doc.docType as DocLinkType} id={doc.id} docNo={doc.docNo} />
                    {/* The supplier's own delivery-note number: what a dispute with them is argued over. */}
                    {doc.ref ? <span style={{ opacity: 0.65 }}> (GDN {doc.ref})</span> : null}
                  </span>
                ))}
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={supplierDispute ? "Ordered" : "Dispatched"}>{d.dispatchedQty}</Descriptions.Item>
          <Descriptions.Item label="Accounted for">{accounted}</Descriptions.Item>
          {expected > 0 && (
            <Descriptions.Item label="Flagged as still expected">
              {expected} — a note, not a settlement; it does not close this on its own
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Closed">{d.closedAt ? new Date(d.closedAt).toLocaleString() : "—"}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Dispositions">
        <Table
          rowKey="id"
          dataSource={d.dispositions ?? []}
          pagination={false}
          locale={{ emptyText: "No dispositions recorded yet." }}
          columns={[
            { title: "Type", dataIndex: "type", render: (v: string) => <Tag>{v.replace(/_/g, " ")}</Tag> },
            { title: "Qty", dataIndex: "qty" },
            { title: "Posted txn", render: (_, r) => r.stockTxnId ?? "— (no movement)" },
            { title: "Reason", dataIndex: "reason", render: (v) => v || "—" },
          ]}
        />
      </Card>

      <Modal title="Record disposition" open={open} onCancel={() => setOpen(false)} onOk={addDisposition} confirmLoading={loading} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="Outcome" rules={[{ required: true }]}>
            <Select options={dispositions.map((x) => ({ value: x.value, label: x.label }))} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const type = form.getFieldValue("type") as DispositionType | undefined;
              const meta = dispositions.find((x) => x.value === type);
              return meta ? <Alert type="info" showIcon style={{ marginBottom: 16 }} message={meta.help} /> : null;
            }}
          </Form.Item>
          <Form.Item name="qty" label="Quantity" rules={[{ required: true }]} extra={`${outstanding} still unaccounted for.`}>
            <InputNumber min={0} max={outstanding} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true, message: "Every unit needs a documented reason." }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
